import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  HStack,
  VStack,
  Image,
  Icon,
  AspectRatio,
  IconButton,
  Circle,
  Link,
  chakra,
} from "@chakra-ui/react";
import {
  FaRegHeart,
  FaRegComment,
  FaRegPaperPlane,
  FaArrowRight,
  FaRegBookmark,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa6";
import { FaCheckCircle, FaEllipsisH } from "react-icons/fa";
import type { AdPreviewCollaboration } from "../../utils/adPreviewCollaboration";
import CollaborationIdentityDisplay from "./CollaborationIdentityDisplay";

const Video = chakra("video");

// Helper function to determine if a URL is a video
const isVideoUrl = (url: string): boolean => {
  if (!url) return false;
  const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".avi", ".mkv"];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some((ext) => lowerUrl.includes(ext));
};

export interface InstagramPostPreviewProps {
  username: string;
  message: string;
  ctaText: string;
  mediaUrls: string[]; // Always an array - component determines single vs carousel
  mediaType: "single" | "carousel";
  profilePictureUrl?: string;
  collaboration?: AdPreviewCollaboration;
  fullWidth?: boolean;
}

const InstagramPostPreview: React.FC<InstagramPostPreviewProps> = ({
  username,
  message,
  ctaText,
  mediaUrls,
  mediaType,
  profilePictureUrl,
  collaboration,
  fullWidth = false,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isTextExpanded, setIsTextExpanded] = useState(false);

  // Text truncation settings
  const MAX_LINES = 2;
  const MAX_CHARACTERS = 150;
  const actualMessage =
    message ||
    "Here is the primary text of 125 characters (optimal length for this)";

  // Calculate if text should be truncated based on lines and characters
  const lines = actualMessage.split("\n");
  const shouldTruncateByLines = lines.length > MAX_LINES;
  const shouldTruncateByChars = actualMessage.length > MAX_CHARACTERS;
  const shouldTruncate = shouldTruncateByLines || shouldTruncateByChars;

  const displayText = (() => {
    if (!shouldTruncate || isTextExpanded) {
      return actualMessage;
    }

    if (shouldTruncateByLines) {
      // Truncate by lines first
      const truncatedLines = lines.slice(0, MAX_LINES);
      return truncatedLines.join("\n") + "...";
    } else {
      // Truncate by characters
      return actualMessage.substring(0, MAX_CHARACTERS) + "...";
    }
  })();

  // Use the mediaUrls array directly - component determines if it's carousel based on length
  const mediaItems = mediaUrls || [];
  const currentMedia = mediaItems[currentImageIndex];
  const isCarousel = mediaItems.length > 1;

  // Reset current image index when media changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [mediaItems]);

  const handlePrevious = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? mediaItems.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentImageIndex((prev) =>
      prev === mediaItems.length - 1 ? 0 : prev + 1
    );
  };
  return (
    <Box
      bg="white"
      borderRadius="lg"
      overflow="hidden"
      w="full"
      boxShadow="lg"
      maxW={fullWidth ? "100%" : ["100%", "70%"]}
    >
      {/* Header */}
      <HStack p={3} spacing={3} alignItems="center">
        <CollaborationIdentityDisplay
          platform="instagram"
          primaryName={username}
          primaryProfilePictureUrl={profilePictureUrl}
          collaboration={collaboration}
          size="md"
        />
        {!collaboration && (
          <Icon as={FaCheckCircle} color="blue.500" fontSize="xs" flexShrink={0} />
        )}
        <Icon as={FaEllipsisH} color="gray.600" fontSize="sm" flexShrink={0} />
      </HStack>

      {/* Content Area */}
      <AspectRatio ratio={1}>
        <Box bg="gray.100" position="relative">
          {currentMedia ? (
            isVideoUrl(currentMedia) ? (
              <Video
                src={currentMedia}
                w="full"
                h="full"
                objectFit="cover"
                autoPlay
                muted
                loop
              />
            ) : (
              <Image
                src={currentMedia}
                alt="Instagram post content"
                w="full"
                h="full"
                objectFit="cover"
              />
            )
          ) : (
            <Box
              w="full"
              h="full"
              bg="gray.200"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Text color="gray.500" fontSize="sm">
                {isCarousel ? "Carousel Content" : "Image/Video"}
              </Text>
            </Box>
          )}

          {/* Carousel Navigation Arrows */}
          {isCarousel && (
            <>
              {/* Left Arrow */}
              <IconButton
                aria-label="Previous image"
                icon={<FaChevronLeft />}
                position="absolute"
                left="2"
                top="50%"
                transform="translateY(-50%)"
                size="sm"
                bg="rgba(0, 0, 0, 0.5)"
                color="white"
                borderRadius="full"
                _hover={{ bg: "rgba(0, 0, 0, 0.7)" }}
                onClick={handlePrevious}
                zIndex={1}
              />

              {/* Right Arrow */}
              <IconButton
                aria-label="Next image"
                icon={<FaChevronRight />}
                position="absolute"
                right="2"
                top="50%"
                transform="translateY(-50%)"
                size="sm"
                bg="rgba(0, 0, 0, 0.5)"
                color="white"
                borderRadius="full"
                _hover={{ bg: "rgba(0, 0, 0, 0.7)" }}
                onClick={handleNext}
                zIndex={1}
              />
            </>
          )}

          {/* Pagination Dots */}
          {isCarousel && (
            <HStack
              position="absolute"
              bottom="2"
              left="50%"
              transform="translateX(-50%)"
              spacing={1}
              zIndex={1}
            >
              {mediaItems.map((_, index) => (
                <Circle
                  key={index}
                  size="6px"
                  bg={
                    index === currentImageIndex
                      ? "white"
                      : "rgba(255, 255, 255, 0.5)"
                  }
                  cursor="pointer"
                  onClick={() => setCurrentImageIndex(index)}
                  transition="all 0.2s"
                  _hover={{ bg: "rgba(255, 255, 255, 0.8)" }}
                  boxShadow="0 1px 3px rgba(0, 0, 0, 0.3)"
                  border="1px solid rgba(0, 0, 0, 0.1)"
                />
              ))}
            </HStack>
          )}
        </Box>
      </AspectRatio>

      {/* Call to Action Bar */}
      <HStack
        bg="white"
        p={3}
        justifyContent="space-between"
        alignItems="center"
        borderTop="1px solid"
        borderBottom="1px solid"
        borderColor="gray.200"
      >
        <Text fontSize="sm" fontWeight="medium">
          {ctaText}
        </Text>
        <Icon as={FaArrowRight} color="gray.600" fontSize="sm" />
      </HStack>

      {/* Interaction Bar and Caption */}
      <VStack spacing={2} p={3} alignItems="stretch">
        {/* Interaction Icons */}
        <HStack justifyContent="space-between" alignItems="center">
          <HStack spacing={4}>
            <Icon as={FaRegHeart} color="gray.600" fontSize="lg" />
            <Icon as={FaRegComment} color="gray.600" fontSize="lg" />
            <Icon as={FaRegPaperPlane} color="gray.600" fontSize="lg" />
          </HStack>
          <Icon as={FaRegBookmark} color="gray.600" fontSize="lg" />
        </HStack>

        {/* Caption */}
        <VStack
          spacing={1}
          alignItems="flex-start"
          wordBreak="break-word"
          overflowWrap="break-word"
        >
          <Text
            fontSize="sm"
            color="gray.800"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
            overflowWrap="break-word"
          >
            <Text as="span" fontWeight="semibold">
              {username}
            </Text>{" "}
            {displayText}
          </Text>
          {shouldTruncate && (
            <Link
              fontSize="xs"
              color="gray.600"
              cursor="pointer"
              onClick={() => setIsTextExpanded(!isTextExpanded)}
              _hover={{ textDecoration: "underline" }}
              display="block"
              mt={1}
            >
              {isTextExpanded ? "Show less" : "Show more"}
            </Link>
          )}
        </VStack>
      </VStack>
    </Box>
  );
};

export default InstagramPostPreview;
